// mediaTagging/view/TagBoxView.js — createTagBoxView: the presentation seam for the tagging UI.
//
// Vorphase (Weg 2, plan mediatagging_topo2_fusion, phase tag-view-extraction): the DOM/presentation
// responsibility used to sit inside TagBoxJQueryAdapter, mixed with interaction, plugin lifecycle and
// transient state — which is why styling the tag UI was so entangled. This view owns ONLY pure
// presentation on the tag layer: no plugins, no state, no interaction wiring.
//
// Form: factory (createXView), matching the sibling views createListNav/createListPanel/createOverlayRowsView
// and the module-structure conventions (rules/javascript_vite.md). The adapter owns/creates the tag layer
// and injects it here; the view closes over it.
//
// Schritt 1: the self-contained layer effects (configure, filter-toggle, flash).
// Schritt 2: build() — the tag box DOM skeleton (elements + topo2 classes + inline positioning). It only
// BUILDS and returns element references; the adapter binds all behavior (handlers, drag/resize, mediator)
// onto them. No handlers, no plugins, no state here.

// How a tag box is addressed in the DOM. Single place on purpose: the id is now the real tag_id — or a NEGATIVE
// placeholder while the box has never been saved — and `#-1` is not a valid CSS selector. An attribute selector
// takes any value, and it also frees the `id` attribute, which was never read anywhere (checked 2026-09-18).
// How long a saved box flashes green/red — a PDF page change waits for it (TaggingCore.commitChanges waitForFlash).
export const FLASH_MS = 500;

export function tagBoxSelector(id) {
    return '[data-id="' + id + '"]';
}

export function createTagBoxView({ $tagLayer }) {
    // Builds the tag box DOM and returns the element references the adapter wires behavior onto.
    // level is already resolved by the caller; isVideo gates the video-only time input.
    // readOnly (tagsRead without tagsWrite): no close button, no level picker, no time field, keyword field not
    // editable — those elements come back as null and the adapter binds nothing onto them.
    function build(id, pix, { text, level, isVideo, readOnly = false } = {}) {
        const $ = window.$;

        const $closeButton = readOnly ? null : $('<button/>', {
            // topo2-Fusion: topo2 icon button, solid + noborder variant, size step xxsmall (15px, User 2026-10-09). Mask X =
            // currentColor (on-accent via --solid). Positioning inline (absolute top-right) replaces the removed
            // legacy .tagBox button float/margin.
            // The size comes from the size step class: topo2 moved the size out of the button variants into its own
            // step when the size system was introduced, and `topo2-btn--xsmall` — what stood here — has not
            // existed since. Nothing matched it, so the button silently rendered at the 40px :root default.
            html: '<span class="topo2-ic topo2-ic--close"></span>',
            class: 'topo2-btn topo2-btn--icon topo2-btn--xxsmall topo2-btn--solid topo2-btn--noborder',
        }).css({ 'z-index': 1001, position: 'absolute', top: -2, right: -2 });

        const $timeInput = isVideo && !readOnly
            ? $('<input/>', { type: 'text', inputmode: 'numeric', class: 'tagTimeInput topo2-field-textinput', placeholder: '0:00' })
                // topo2-Fusion: visual via .topo2-field-textinput; only positioning stays inline.
                //
                // Sits BELOW the keyword box since 2026-09-28 (it used to overlay the frame's bottom edge and
                // covered the picture there). It is a CHILD of that box, positioned at `top: 100%`, so it
                // follows the box's lower edge by itself as the keyword text wraps and the box grows taller.
                // A fixed offset does NOT work: the box is anchored by its TOP — the adapter writes an inline
                // `top` (TagBoxJQueryAdapter, showInput), and with top+height set the CSS `bottom: -25px` is
                // ignored — so it grows DOWNWARDS and would run into a field parked at a fixed distance.
                // Being a child costs one scoped CSS rule in topo2/tag-box.css: the legacy
                // `.tagBox .input * { border: none }` would otherwise strip the white 4px spacer borders that
                // give a topo2 field its height.
                .css({ display: 'none', position: 'absolute', top: '100%', marginTop: 2,
                       left: '50%', transform: 'translateX(-50%)',
                       width: 75, textAlign: 'center', zIndex: 1001 })
            : null;

        const $select = readOnly ? null : $('<select/>')
            .append(
                $('<option/>', { value: 1, html: 1 }),
                $('<option/>', { value: 2, html: 2 })
            )
            .val(level ?? 2);

        // (P12) The keyword field: a contenteditable div in the topo2 white-bg field style. It replaced the old
        // textarea + its jQuery plugins (hwt/dynAutocomplete) — the adapter wires the new keywordAutocomplete + the
        // live hidden-keyword highlight onto it. NOT class 'keywords', so no legacy .keywords handler fires on it.
        // flex:1 lets it fill the row next to the level <select>.
        const $ce = $('<div/>', { class: 'keywords-ce topo2-field-textarea', contenteditable: readOnly ? 'false' : 'true' })
            .text(text ?? '')
            .css({
                flex: '1 1 auto', minWidth: 0, width: '100%', outline: 'none',
                'min-height': 15, 'max-height': 122, 'overflow-y': 'auto', 'white-space': 'pre-wrap',
            });

        // (P12 tweak) No fixed inline height — the CSS `.tagBox .input { height: fit-content }` grows the panel as
        // $ce wraps (bounded by $ce's max-height). Default align-items:stretch lets the level picker fill the height.
        const $input = $('<div style="display: none;" />')
            .css({ 'z-index': 1001 })
            .addClass('input')
            // The time field is absolutely positioned, so it leaves the flex row and renders below the box —
            // it is a child only so that it tracks the box's lower edge (see its .css above).
            .append($select || [], $ce, $timeInput || []);

        const $tagBox = $('<div/>')
            .attr('data-id', id)
            .addClass('tagBox')
            .toggleClass('tagBox--readonly', readOnly)   // styling hook only — no rule needed yet
            .css({ left: pix.l, top: pix.t, width: pix.w, height: pix.h })
            .data('id', id)
            .append($closeButton || [])
            .append($input);

        return { $tagBox, $input, $ce, $select, $closeButton, $timeInput };
    }

    // Sets up the tag layer element on top of the media image (size/position + drag-area classes).
    function configureLayer($img) {
        $tagLayer
            .addClass('dragArea tagLayer')
            .css({
                width:      $img.width(),
                height:     $img.height(),
                position:   'absolute',
                top:        0,
                left:       0,
                background: 'url(/statimg/null.gif)',
            });
    }

    // Toggles each tagBox's visibility according to the given filter.
    function applyTagFilter(tags, filter) {
        tags.forEach(tag => {
            $tagLayer.find(tagBoxSelector(tag.id)).toggle(filter(tag));
        });
    }

    // Briefly flashes a tagBox green (success) or red (failure) after a save/delete.
    function flashTagBox(tagId, success) {
        const $box = $tagLayer.find(tagBoxSelector(tagId));
        $box.css({ 'background-color': success ? '#95e133' : '#c63c4c', opacity: 0.5 });
        setTimeout(() => {
            $box.css({ 'background-color': 'rgba(0, 0, 0, 0)', opacity: 1 });
        }, FLASH_MS);
    }

    return { build, configureLayer, applyTagFilter, flashTagBox };
}
